import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Sparkles, Loader2, RefreshCw } from "lucide-react";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { fmtNum, fmtPct, changeClass } from "../lib/format";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";

const KIND_LABEL = { index: "INDEX", volatility: "VOLATILITY", rates: "US 10Y YIELD", fx: "FX", commodity: "COMMODITY" };

const IndicatorCard = ({ m }) => (
  <div className="widget p-4" data-testid={`macro-card-${m.symbol.replace(/[\^=]/g, "")}`}>
    <div className="flex items-center justify-between">
      <div className="overline-label">{m.name}</div>
      <span className="text-[10px] font-mono text-muted-foreground/60">{KIND_LABEL[m.kind] || m.kind}</span>
    </div>
    <div className="flex items-end justify-between mt-2 gap-2">
      <div>
        <div className="font-mono text-xl text-white">{fmtNum(m.price)}</div>
        <div className="flex gap-3 mt-1 text-xs font-mono">
          <span className={changeClass(m.change_pct)}>{fmtPct(m.change_pct)} 1d</span>
          {m.change_1m_pct != null && <span className={changeClass(m.change_1m_pct)}>{fmtPct(m.change_1m_pct)} 1m</span>}
        </div>
      </div>
      <div className="w-24 h-10">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={m.spark.map((v) => ({ v }))}>
            <Line type="monotone" dataKey="v" stroke={m.change_1m_pct >= 0 ? "#10b981" : "#ef4444"} strokeWidth={1.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
);

const toneClass = (t) => (t === "positive" ? "text-gain" : t === "negative" ? "text-loss" : "text-yellow-500");
const sentimentChip = (s) =>
  s === "positive" ? "bg-gain/10 text-gain border-gain/30"
    : s === "negative" ? "bg-loss/10 text-loss border-loss/30"
    : "bg-yellow-500/10 text-yellow-500 border-yellow-500/30";

const BriefingPanel = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/macro/briefing");
      setResult(data);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="widget p-6 bg-blue-950/20 border-primary/20" data-testid="briefing-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h2 className="font-heading font-bold text-lg text-white tracking-tight">AI Daily Market Briefing</h2>
        </div>
        {result && (
          <span className="text-[11px] font-mono text-muted-foreground">
            {result.cached ? "CACHED · " : "FRESH · "}{new Date(result.generated_at).toLocaleString()}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        Claude synthesises the macro snapshot and today's cached headlines into themes and sector tone.
      </p>
      {!result ? (
        <Button onClick={run} disabled={loading} className="rounded-sm bg-primary hover:bg-primary/85 text-white" data-testid="briefing-button">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing briefing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate briefing</>}
        </Button>
      ) : (
        <div className="space-y-5 fade-up" data-testid="briefing-content">
          <p className="text-sm text-gray-100 leading-relaxed">{result.briefing.summary}</p>
          <div>
            <div className="overline-label mb-2">Key themes</div>
            <div className="flex flex-wrap gap-2">
              {result.briefing.key_themes.map((t, i) => (
                <Badge key={i} variant="outline" className="rounded-sm border-primary/40 text-primary font-mono text-xs">{t}</Badge>
              ))}
            </div>
          </div>
          <div>
            <div className="overline-label mb-2">Sector tone</div>
            <div className="space-y-1.5">
              {result.briefing.sector_notes.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className={`font-mono text-xs uppercase w-24 shrink-0 mt-0.5 ${toneClass(s.tone)}`}>{s.sector}</span>
                  <span className="text-gray-300">{s.note}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="overline-label mb-2">What to watch</div>
            <ul className="space-y-1">
              {result.briefing.watch_items.map((w, i) => (
                <li key={i} className="text-sm text-gray-300 pl-3 border-l border-primary/40">{w}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-5 pt-3 border-t border-white/10">
        This is AI-generated research, not financial advice.
      </p>
    </div>
  );
};

const SentimentPanel = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.get("/macro/sentiment").then((r) => setData(r.data));
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.post("/macro/sentiment/refresh");
      setData(d);
      toast.success(d.cached ? "Sentiment is fresh (cached)" : `Scored ${d.count} tickers`);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setRefreshing(false);
    }
  };

  const entries = data ? Object.entries(data.sentiment).sort((a, b) => a[0].localeCompare(b[0])) : [];

  return (
    <div className="widget" data-testid="sentiment-panel">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="overline-label">News sentiment by ticker</span>
          {data?.generated_at && (
            <span className="text-[10px] font-mono text-muted-foreground/60">as of {new Date(data.generated_at).toLocaleString()}</span>
          )}
        </div>
        <Button onClick={refresh} disabled={refreshing} variant="outline"
          className="rounded-sm h-8 text-xs gap-1.5 border-border" data-testid="sentiment-refresh-button">
          {refreshing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scoring headlines (≈1 min)…</> : <><RefreshCw className="w-3.5 h-3.5" /> Refresh scores</>}
        </Button>
      </div>
      {!data ? (
        <div className="p-4"><Skeleton className="h-24 bg-secondary" /></div>
      ) : entries.length === 0 ? (
        <p className="p-8 text-sm text-muted-foreground" data-testid="sentiment-empty">
          No sentiment scores yet — click "Refresh scores" to have Claude read the cached headlines for every ticker.
        </p>
      ) : (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {entries.map(([ticker, v]) => (
            <Link key={ticker} to={`/stock/${ticker}`}
              className={`border rounded-sm px-3 py-2 flex flex-col gap-0.5 hover:bg-white/5 transition-colors duration-150 ${sentimentChip(v.sentiment)}`}
              data-testid={`sentiment-chip-${ticker}`}>
              <span className="flex items-center justify-between">
                <span className="font-mono text-sm font-medium">{ticker}</span>
                <span className="text-[10px] font-mono uppercase">{v.sentiment}</span>
              </span>
              <span className="text-[11px] text-muted-foreground leading-snug">{v.rationale}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Macro() {
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    api.get("/macro/overview").then((r) => setOverview(r.data));
  }, []);

  return (
    <div className="space-y-8 fade-up">
      <div>
        <div className="overline-label mb-2">Macro &amp; sentiment</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white flex items-center gap-3">
          Economic News <Newspaper className="w-8 h-8 text-primary" />
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="macro-indicators-grid">
        {!overview
          ? [0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-28 rounded-sm bg-card" />)
          : overview.indicators.map((m) => <IndicatorCard key={m.symbol} m={m} />)}
      </div>

      <BriefingPanel />
      <SentimentPanel />
    </div>
  );
}
